/*  ============================================================================
    ItGlueUpload.jsx — import an incumbent MSP's runbook into IT Glue.
    Autotask → "IT Glue Upload".

    Takes the AI-extracted runbook JSON (passwords / documents / configurations),
    lets the operator review and re-classify every line, dry-runs it, and then
    imports it record by record.

    ⚠ A DOCUMENT, ONCE CREATED, CANNOT BE DELETED through IT Glue's API, and
      nothing can be filed into a folder. That is why the flow is Upload →
      Review → Dry run → Import rather than a one-click importer, why the dry
      run is mandatory, and why the review table defaults every uncertain row to
      Skip rather than to Create.

    ⚠ What IS fixable, corrected 2026-09-03: a document's BODY. The body is a
      Document::Text SECTION, sections are writable, and a section can be
      DELETED — so a wrong body can be replaced. Only the document's EXISTENCE
      is permanent. Do not re-broaden this back to "documents cannot be
      corrected"; that over-reading nearly cost the whole document feature.

    ⚠ CREDENTIALS. The extraction holds live plaintext passwords — its own
      metadata says so. They are parsed and held SERVER-SIDE; the rows this
      component receives carry names, usernames and flags but never a value.
      Revealing one is a per-value audited call, exactly like revealing a
      password that is already in IT Glue. Nothing from an upload is written to
      the app state blob: another MSP's credentials have no business in storage.

    ⚠ WHY A SEPARATE COMPONENT, not another block inside MspOsApp.jsx: that file
      is 16k lines and is edited by other work in parallel. Self-contained costs
      MspOsApp three lines and keeps this reviewable.

    Out of scope for v1, deliberately:
      • FLEXIBLE ASSETS (Firewall, Wi-Fi, Server, Licensing, … — the ~20 asset
        types in IT Glue's sidebar). They are a different API with a per-type
        dynamic field schema, and NONE of the 78 entries in the sample runbook
        is one. Rows of an unsupported type default to Skip and say so.
      • Document folders — unavailable to an API key (JWT only), so documents
        land at the org root.
      • Correcting or deleting anything already imported. Not possible; the UI
        never implies it is.
    ============================================================================ */

import { useState, useCallback, useMemo, useRef } from "react";
import ig from "../../api/itGlueApi";

const errMsg = (e) =>
  e?.response?.data?.error || (e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || "request failed"));

const TYPE_LABEL = { password: "Password", document: "Document", configuration: "Configuration", skip: "Skip" };

const STATUS_STYLE = {
  created:              { color: "var(--ops)",    label: "Created" },
  matched:              { color: "var(--strat)",  label: "Matched" },
  "created-unverified": { color: "var(--warn)",   label: "Created (unverified)" },
  "created-empty-body": { color: "var(--danger)", label: "Body did not read back" },
  "already-exists":     { color: "var(--text3)",  label: "Already in IT Glue" },
  "already-imported":   { color: "var(--text3)",  label: "Already imported" },
  skipped:              { color: "var(--text3)",  label: "Skipped" },
  "no-match":           { color: "var(--warn)",   label: "No match" },
  abandoned:            { color: "var(--danger)", label: "Abandoned" },
  failed:               { color: "var(--danger)", label: "Failed" },
};

const RESOLUTION_STYLE = {
  "will-create":     { color: "var(--ops)",    label: "Will create" },
  "will-match":      { color: "var(--strat)",  label: "Will match" },
  "duplicate-name":  { color: "var(--warn)",   label: "Duplicate name — skipped" },
  "no-match":        { color: "var(--warn)",   label: "No match — nothing written" },
  blocked:           { color: "var(--danger)", label: "Blocked" },
  "already-imported":{ color: "var(--text3)",  label: "Already imported" },
  skip:              { color: "var(--text3)",  label: "Skip" },
};

const card = { background: "white", border: "1px solid var(--border)", borderRadius: 8, padding: "14px 16px", marginBottom: 12 };
const hdr = { fontSize: 12, fontWeight: 700, marginBottom: 6 };
const sub = { fontSize: 9.5, color: "var(--text3)", lineHeight: 1.5 };
const th = { padding: "5px 8px", textAlign: "left", color: "var(--text3)", fontSize: 9, textTransform: "uppercase", letterSpacing: ".08em", whiteSpace: "nowrap" };
const td = { padding: "6px 8px", verticalAlign: "top" };

function Banner({ kind, children }) {
  const c = kind === "danger" ? "var(--danger)" : kind === "warn" ? "var(--cal)" : "var(--strat)";
  const bg = kind === "danger" ? "var(--danger-d)" : kind === "warn" ? "var(--cal-d)" : "var(--strat-d)";
  return (
    <div style={{ padding: "8px 12px", background: bg, border: `1px solid ${c}33`, borderRadius: 6,
                  fontSize: 10.5, color: c, marginBottom: 10, lineHeight: 1.55 }}>
      {children}
    </div>
  );
}

/** Read a File to base64 without the data: prefix — that prefix 422s the
 *  IT Glue image endpoint, and the backend strips it defensively too. */
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).replace(/^data:[^;]+;base64,/, ""));
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function ItGlueUpload() {
  const [orgs, setOrgs] = useState(null);
  const [orgId, setOrgId] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [configTypes, setConfigTypes] = useState(null);

  const [staging, setStaging] = useState(null);   // { id, meta, rows, warnings }
  const [rows, setRows] = useState([]);           // the editable review queue
  const [contents, setContents] = useState({});   // exportId -> full document text
  const [images, setImages] = useState({});       // basename -> base64
  const [revealed, setRevealed] = useState({});   // exportId -> value (transient)
  const [editing, setEditing] = useState({});     // exportId -> value box is open

  const [plan, setPlan] = useState(null);
  const [results, setResults] = useState(null);

  // Post-import history. The run report used to live only in `results` above —
  // React state — so it was gone on a tab close, a refresh, or a switch to
  // another MSP OS tab, taking the list of what was left undone with it.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [runs, setRuns] = useState(null);
  const [openRun, setOpenRun] = useState(null);       // the run detail on screen
  const [runFollowUpOnly, setRunFollowUpOnly] = useState(true);
  const [thisOrgOnly, setThisOrgOnly] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const fileRef = useRef(null);

  /* ── Organisations ────────────────────────────────────────────────────────
     NOT the Autotask-name match used by the mobile app. These are new clients
     being onboarded: the IT Glue org may not exist yet, or may not carry the
     Autotask name. Picking from a list removes the string to get wrong — and a
     wrong org here means another client's credentials. */
  const loadOrgs = useCallback(() => {
    setBusy("orgs"); setError(null);
    Promise.all([ig.importOrgs(), ig.importConfigTypes().catch(() => null)])
      .then(([o, t]) => { setOrgs(o); setConfigTypes(t); })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setBusy(null));
  }, []);

  /* ── History ────────────────────────────────────────────────────────────── */
  const loadHistory = useCallback((orgFilter) => {
    setBusy("history"); setError(null);
    ig.importHistory(orgFilter || undefined)
      .then(setRuns)
      .catch((e) => setError(errMsg(e)))
      .finally(() => setBusy(null));
  }, []);

  const toggleHistory = useCallback(() => {
    const next = !historyOpen;
    setHistoryOpen(next);
    setOpenRun(null);
    if (next && !runs) loadHistory(thisOrgOnly ? orgId : null);
  }, [historyOpen, runs, loadHistory, thisOrgOnly, orgId]);

  const showRun = useCallback((runId) => {
    if (openRun && openRun.runId === runId) { setOpenRun(null); return; }
    setBusy("run"); setError(null); setRunFollowUpOnly(true);
    ig.importHistoryRun(runId)
      .then(setOpenRun)
      .catch((e) => setError(errMsg(e)))
      .finally(() => setBusy(null));
  }, [openRun]);

  /* ── Upload ─────────────────────────────────────────────────────────────── */
  const onFile = useCallback(async (file) => {
    if (!file) return;
    setBusy("stage"); setError(null); setPlan(null); setResults(null);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      // The full document bodies stay in the browser only so the operator can
      // read them before importing; the backend already holds its own copy.
      const bodies = {};
      const entries = Array.isArray(json) ? json : (json.entries || []);
      entries.forEach((e) => {
        if (e?.attributes?.content_plain) bodies[e.export_id] = e.attributes.content_plain;
      });
      const staged = await ig.importStage(json);
      setStaging(staged);
      setRows(staged.rows.map((r) => ({ ...r })));
      setContents(bodies);
      setRevealed({});
    } catch (e) {
      setError(e instanceof SyntaxError ? "That file is not valid JSON." : errMsg(e));
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, []);

  const onImages = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy("images");
    try {
      const next = { ...images };
      for (const f of files) next[f.name] = await fileToBase64(f);
      setImages(next);
      setPlan(null);
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(null); }
  }, [images]);

  const discard = useCallback(() => {
    if (staging) ig.importDiscard(staging.id).catch(() => {});
    setStaging(null); setRows([]); setContents({}); setImages({});
    setRevealed({}); setPlan(null); setResults(null); setError(null);
  }, [staging]);

  /* ── Review edits ───────────────────────────────────────────────────────── */
  /* Rows stay editable AFTER a run, deliberately. A partial import is the case
     that most needs fixing and re-running — a row that failed, or that clashed
     on a name — and freezing the queue would leave the operator retyping the
     whole upload. What makes that safe is the stage's created-map, not a
     disabled <select>: a re-run skips everything already created. The Result
     column keeps showing what landed, and any edit clears the dry run, so a
     second run always goes through a fresh one. */
  const patchRow = useCallback((exportId, patch) => {
    setRows((rs) => rs.map((r) => (r.exportId === exportId ? { ...r, ...patch } : r)));
    setPlan(null);      // any edit invalidates the dry run — and the run needs one
  }, []);

  const setType = (row, targetType) => {
    const patch = { targetType };
    // Re-derive the action from the new type, keeping the safe default: a
    // configuration matches, a valueless password skips.
    if (targetType === "skip") patch.action = "skip";
    else if (targetType === "configuration") patch.action = "match";
    else if (targetType === "password") patch.action = row.hasValue || row.password ? "create" : "skip";
    else patch.action = "create";
    patchRow(row.exportId, patch);
  };

  const reveal = useCallback((exportId) => {
    if (revealed[exportId] !== undefined) { setRevealed((v) => { const n = { ...v }; delete n[exportId]; return n; }); return; }
    ig.importReveal(staging.id, exportId)
      .then((value) => {
        setRevealed((v) => ({ ...v, [exportId]: value }));
        // Auto-hide, matching the existing reveal behaviour — a value should not
        // sit on screen behind whoever walks past next.
        setTimeout(() => setRevealed((v) => { const n = { ...v }; delete n[exportId]; return n; }), 20000);
      })
      .catch((e) => setError(errMsg(e)));
  }, [staging, revealed]);

  /* A value the operator types is the ONE case where a credential travels
     browser -> server. Setting one on a row that had none flips it to Create:
     typing a value is unambiguous intent, and leaving it on Skip would quietly
     throw the work away. */
  const setValue = useCallback((row, value) => {
    patchRow(row.exportId, { password: value, action: value ? "create" : (row.hasValue ? "create" : "skip") });
  }, [patchRow]);

  /* ── Dry run and import ─────────────────────────────────────────────────── */
  // The rows sent up carry the operator's edits and the full document bodies.
  const rowsForApi = useMemo(() => rows.map((r) => ({
    ...r,
    contentPlain: contents[r.exportId] != null ? contents[r.exportId] : undefined,
    attachedImages: (r.imageFiles || []).filter((f) => images[String(f).split("/").pop()]).length,
  })), [rows, contents, images]);

  const dryRun = useCallback(() => {
    setBusy("plan"); setError(null); setResults(null);
    ig.importPlan(staging.id, orgId, rowsForApi)
      .then(setPlan)
      .catch((e) => setError(errMsg(e)))
      .finally(() => setBusy(null));
  }, [staging, orgId, rowsForApi]);

  const runImport = useCallback(() => {
    const orgName = (orgs || []).find((o) => String(o.id) === String(orgId))?.name || orgId;
    const t = plan?.totals;
    const n = (t?.willCreate?.password || 0) + (t?.willCreate?.document || 0) + (t?.willCreate?.configuration || 0);
    // The last gate, and it names the org out loud — the mistake that matters
    // most here is importing a runbook into the wrong client.
    if (!window.confirm(
      `Import ${n} record${n === 1 ? "" : "s"} into “${orgName}”?\n\n` +
      `This cannot be undone. IT Glue's API cannot delete or edit a document — anything ` +
      `created has to be removed by hand in the IT Glue UI.`
    )) return;

    setBusy("run"); setError(null);
    ig.importRun(staging.id, orgId, rowsForApi, images)
      .then((out) => {
        setResults(out); setPlan(null);
        // The run is already persisted server-side; drop the cached list so the
        // history reflects it the moment it is opened.
        setRuns(null);
        if (historyOpen) loadHistory(thisOrgOnly ? orgId : null);
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setBusy(null));
  }, [staging, orgId, rowsForApi, images, plan, orgs, historyOpen, loadHistory, thisOrgOnly]);

  /* ── Derived ────────────────────────────────────────────────────────────── */
  const counts = useMemo(() => {
    const c = { password: 0, document: 0, configuration: 0, skip: 0 };
    rows.forEach((r) => {
      const key = r.action === "skip" ? "skip" : r.targetType;
      if (c[key] !== undefined) c[key] += 1;
    });
    return c;
  }, [rows]);

  const visibleOrgs = useMemo(() => {
    const list = orgs || [];
    const q = orgFilter.trim().toLowerCase();
    return q ? list.filter((o) => o.name.toLowerCase().includes(q)) : list;
  }, [orgs, orgFilter]);

  const planByRow = useMemo(() => {
    const m = {};
    (plan?.items || []).forEach((i) => { m[i.exportId] = i; });
    return m;
  }, [plan]);

  const resultByRow = useMemo(() => {
    const m = {};
    (results?.results || []).forEach((i) => { m[i.exportId] = i; });
    return m;
  }, [results]);

  const canPlan = Boolean(staging && orgId && !busy);
  const canRun = Boolean(plan && plan.canRun && !busy);

  /* ══════════════════════════════════════════════════════════════════════ */

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16, background: "var(--bg)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>

        <div style={{ ...card, marginBottom: 12 }}>
          <div style={hdr}>IT Glue Upload — runbook import</div>
          <div style={sub}>
            Import an incumbent MSP's runbook into IT Glue as Passwords, Documents and Configurations.
            Upload the extracted JSON, review and re-classify every line, dry-run it, then import.
          </div>
          <Banner kind="danger">
            <strong>A document, once created, cannot be deleted</strong> through IT Glue's API, and
            nothing can be filed into a folder — everything lands at the org root. A document's body
            can be corrected afterwards (it is a section, and sections can be replaced), but its
            existence cannot be undone. Read the dry run before importing.
          </Banner>
        </div>

        {/* ── Import history ──────────────────────────────────────────────
             Directly under the intro so it is findable on a standalone visit,
             collapsed by default so it never sits between the operator and the
             import. The run report used to live only in React state, so the
             list of what an import LEFT UNDONE — the index-only passwords, the
             unmatched configurations, the failures — died with the tab. That
             list is the one thing nobody can reconstruct afterwards. ── */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ ...hdr, marginBottom: 0 }}>Import history</div>
            <button className="btn" style={{ fontSize: 10, padding: "4px 9px" }}
              onClick={toggleHistory} disabled={busy === "history"}>
              {busy === "history" ? "Loading…" : historyOpen ? "Hide" : "Show past imports"}
            </button>
            {historyOpen && (
              <>
                <button className="btn" style={{ fontSize: 10, padding: "4px 9px" }}
                  onClick={() => loadHistory(thisOrgOnly ? orgId : null)} disabled={busy === "history"}>
                  ↻ Refresh
                </button>
                {orgId && (
                  <label style={{ ...sub, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                    <input type="checkbox" checked={thisOrgOnly}
                      onChange={(e) => { setThisOrgOnly(e.target.checked); setOpenRun(null); loadHistory(e.target.checked ? orgId : null); }} />
                    only the selected organisation
                  </label>
                )}
              </>
            )}
          </div>
          <div style={{ ...sub, marginTop: 6 }}>
            Every run is kept: what was created, what was matched, and — the part nothing else
            records — what was deliberately left undone. Names and reasons only; no password
            values are ever stored here.
          </div>

          {historyOpen && (
            <div style={{ marginTop: 10 }}>
              {runs && runs.length === 0 && (
                <div style={sub}>No imports recorded yet.</div>
              )}
              {(runs || []).map((r) => {
                const isOpen = openRun && openRun.runId === r.runId;
                return (
                  <div key={r.runId} style={{ borderTop: "1px solid var(--border)", padding: "7px 0" }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                      <button className="btn" style={{ fontSize: 9, padding: "2px 7px" }}
                        onClick={() => showRun(r.runId)} disabled={busy === "run"}>
                        {isOpen ? "hide" : "open"}
                      </button>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>{r.orgName || `Org ${r.orgId}`}</span>
                      <span style={sub}>
                        {new Date(r.finishedAt).toLocaleString("en-AU", {
                          day: "numeric", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                        {r.sourceFile ? ` · ${r.sourceFile}` : ""}
                        {r.msp ? ` · from ${r.msp}` : ""}
                        {r.user ? ` · ${r.user}` : ""}
                      </span>
                      <span style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                        {Object.entries(r.tally || {}).map(([k, v]) => (
                          <span key={k} style={{ fontSize: 9, color: (STATUS_STYLE[k] || {}).color || "var(--text3)" }}>
                            <strong>{v}</strong> {(STATUS_STYLE[k] || {}).label || k}
                          </span>
                        ))}
                        {r.followUp > 0 && (
                          <span style={{ fontSize: 9.5, fontWeight: 700, color: "var(--cal)" }}>
                            {r.followUp} need follow-up
                          </span>
                        )}
                      </span>
                    </div>

                    {r.documentsAbandoned && (
                      <div style={{ fontSize: 9, color: "var(--danger)", marginTop: 3 }}>
                        Documents were abandoned in this run — open it for the reason.
                      </div>
                    )}

                    {isOpen && (
                      <div style={{ marginTop: 8, paddingLeft: 4 }}>
                        {openRun.documentsAbandoned && <Banner kind="danger">{openRun.documentsAbandoned}</Banner>}
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                          <label style={{ ...sub, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                            <input type="checkbox" checked={runFollowUpOnly}
                              onChange={(e) => setRunFollowUpOnly(e.target.checked)} />
                            only rows needing follow-up
                          </label>
                          <span style={sub}>
                            {openRun.rows.filter((x) => !runFollowUpOnly || x.followUp).length} of{" "}
                            {openRun.rows.length} rows
                            {openRun.source && openRun.source.organizationName
                              ? ` · the runbook names its client as “${openRun.source.organizationName}”`
                              : ""}
                          </span>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                            <thead>
                              <tr>
                                <th style={th}>Name</th>
                                <th style={th}>Type</th>
                                <th style={th}>Outcome</th>
                                <th style={th}>Why / what to do</th>
                              </tr>
                            </thead>
                            <tbody>
                              {openRun.rows
                                .filter((x) => !runFollowUpOnly || x.followUp)
                                .map((x) => (
                                  <tr key={x.exportId} style={{ borderTop: "1px solid var(--border)" }}>
                                    <td style={{ ...td, maxWidth: 240 }}>
                                      <div style={{ fontWeight: x.followUp ? 600 : 400 }}>{x.name}</div>
                                      {x.username && <div style={{ fontSize: 9, color: "var(--text3)" }}>{x.username}</div>}
                                      {(x.index || x.sourcePage) && (
                                        <div style={{ fontSize: 8.5, color: "var(--text3)" }}>
                                          {x.index ? `index ${x.index}` : ""}{x.sourcePage ? ` · p${x.sourcePage}` : ""}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ ...td, whiteSpace: "nowrap" }}>{TYPE_LABEL[x.targetType] || x.targetType}</td>
                                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                                      <span style={{ fontWeight: 700, fontSize: 9.5, color: (STATUS_STYLE[x.status] || {}).color || "var(--text3)" }}>
                                        {(STATUS_STYLE[x.status] || {}).label || x.status}
                                      </span>
                                      {x.itGlueId && <div style={{ fontSize: 8.5, color: "var(--text3)" }}>{x.itGlueId}</div>}
                                    </td>
                                    <td style={{ ...td, maxWidth: 420 }}>
                                      {x.message && <div style={{ fontSize: 9, color: "var(--text3)", lineHeight: 1.45 }}>{x.message}</div>}
                                      {(x.flags || []).map((f, i) => (
                                        <div key={i} style={{ fontSize: 9, color: "var(--cal)", lineHeight: 1.45 }}>{f}</div>
                                      ))}
                                      {x.matchedName && (
                                        <div style={{ fontSize: 9, color: "var(--strat)" }}>
                                          matched “{x.matchedName}” on {x.matchedOn}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {error && <Banner kind="danger">{error}</Banner>}

        {/* ── 1. Organisation ─────────────────────────────────────────────── */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ ...hdr, marginBottom: 0 }}>1 · IT Glue organisation</div>
            <button className="btn" style={{ fontSize: 10, padding: "4px 9px" }}
              onClick={loadOrgs} disabled={busy === "orgs"}>
              {busy === "orgs" ? "Loading…" : orgs ? "↻ Reload" : "Load organisations"}
            </button>
            {orgs && <span style={{ ...sub }}>{orgs.length} organisations</span>}
          </div>
          <div style={{ ...sub, margin: "6px 0 8px" }}>
            Picked, not matched on a name. These are clients being onboarded — the IT Glue org may not
            exist yet or may not carry the Autotask company name, and the wrong org here means another
            client's credentials.
          </div>
          {orgs && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}
                placeholder="Filter organisations…"
                style={{ fontSize: 11, padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, minWidth: 200 }} />
              <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setPlan(null); }}
                style={{ fontSize: 11, padding: "5px 8px", border: "1px solid var(--border)", borderRadius: 5, minWidth: 380 }}>
                <option value="">— choose an organisation —</option>
                {visibleOrgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}{o.status ? ` · ${o.status}` : ""}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* ── 2. Upload ───────────────────────────────────────────────────── */}
        <div style={card}>
          <div style={{ ...hdr }}>2 · Runbook extraction</div>
          {!staging ? (
            <>
              <div style={{ ...sub, marginBottom: 8 }}>
                The extraction JSON — one entry per runbook item, each already carrying its IT Glue type,
                category hint and extraction flags. Credentials in it are held server-side and never
                sent back to this screen.
              </div>
              <input ref={fileRef} type="file" accept=".json,application/json"
                onChange={(e) => onFile(e.target.files?.[0])} disabled={busy === "stage"}
                style={{ fontSize: 11 }} />
              {busy === "stage" && <span style={{ ...sub, marginLeft: 8 }}>Parsing…</span>}
            </>
          ) : (
            <>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
                <div style={{ fontSize: 11, fontWeight: 600 }}>{staging.meta.sourceFile || "Runbook extraction"}</div>
                <div style={sub}>
                  {staging.meta.organizationName ? `${staging.meta.organizationName} · ` : ""}
                  {staging.meta.msp ? `from ${staging.meta.msp} · ` : ""}
                  {rows.length} entries
                </div>
                <button className="btn" style={{ marginLeft: "auto", fontSize: 10, padding: "4px 9px" }}
                  onClick={discard} disabled={busy === "run"}>Discard upload</button>
              </div>
              {(staging.warnings || []).map((w, i) => <Banner key={i} kind="warn">{w}</Banner>)}

              <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <label style={{ fontSize: 10.5, fontWeight: 600 }}>Images folder (optional):</label>
                <input type="file" multiple accept="image/*"
                  onChange={(e) => onImages(e.target.files)} disabled={busy === "images"}
                  style={{ fontSize: 10.5 }} />
                <span style={sub}>
                  {Object.keys(images).length
                    ? `${Object.keys(images).length} image(s) attached`
                    : "Documents import as text only without them."}
                </span>
              </div>
            </>
          )}
        </div>

        {/* ── 3. Review queue ─────────────────────────────────────────────── */}
        {staging && (
          <div style={card}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
              <div style={{ ...hdr, marginBottom: 0 }}>3 · Review</div>
              <div style={sub}>
                {counts.password} password · {counts.document} document · {counts.configuration} configuration ·{" "}
                {counts.skip} skip
              </div>
            </div>
            <div style={{ ...sub, margin: "6px 0 10px" }}>
              Every row's target type is editable. Passwords with no value in the source and types v1
              does not import default to <strong>Skip</strong>. Configurations default to{" "}
              <strong>Match</strong> — they are devices IT Glue already auto-discovered, and creating
              them duplicates the estate. Switch a row to Create only when the device is genuinely new.
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Name</th>
                    <th style={th}>Detail</th>
                    <th style={th}>Import as</th>
                    <th style={th}>Action</th>
                    <th style={th}>{results ? "Result" : plan ? "Dry run" : "Flags"}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const p = planByRow[r.exportId];
                    const res = resultByRow[r.exportId];
                    const skipping = r.action === "skip" || r.targetType === "skip";
                    const open = expanded[r.exportId];
                    return (
                      <tr key={r.exportId} style={{ borderTop: "1px solid var(--border)", opacity: skipping ? 0.6 : 1 }}>
                        <td style={{ ...td, color: "var(--text3)", whiteSpace: "nowrap" }}>
                          {r.index || r.seq}
                          {r.sourcePage ? <div style={{ fontSize: 8.5 }}>p{r.sourcePage}</div> : null}
                        </td>

                        <td style={{ ...td, maxWidth: 260 }}>
                          <div style={{ fontWeight: 600 }}>{r.name}</div>
                          {r.categoryHint && <div style={{ fontSize: 9, color: "var(--text3)" }}>{r.categoryHint}</div>}
                          {r.secondaryTypes?.length > 0 && (
                            <div style={{ fontSize: 8.5, color: "var(--text3)", marginTop: 2 }}>
                              also suggests: {r.secondaryTypes.join(", ")} — not imported in v1
                            </div>
                          )}
                        </td>

                        <td style={{ ...td, maxWidth: 320 }}>
                          {r.detectedType === "password" && (
                            <>
                              <div style={{ fontSize: 10 }}>{r.username || <span style={{ color: "var(--text3)" }}>no username</span>}</div>
                              <div style={{ fontSize: 10, fontFamily: "var(--ff-mono)", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                {editing[r.exportId] ? (
                                  <input type="text" autoFocus value={r.password || ""}
                                    onChange={(e) => setValue(r, e.target.value)}
                                    placeholder="paste the value"
                                    style={{ fontSize: 10, fontFamily: "var(--ff-mono)", padding: "3px 6px",
                                             border: "1px solid var(--border)", borderRadius: 4, minWidth: 170 }} />
                                ) : r.password ? (
                                  <span style={{ color: "var(--ops)", fontWeight: 600 }}>value set here</span>
                                ) : !r.hasValue ? (
                                  <span style={{ color: "var(--cal)", fontWeight: 600 }}>no value in source</span>
                                ) : revealed[r.exportId] !== undefined ? (
                                  <span style={{ color: "var(--danger)" }}>{revealed[r.exportId]}</span>
                                ) : (
                                  <span style={{ letterSpacing: 2 }}>••••••••</span>
                                )}
                                {r.hasValue && !r.password && !editing[r.exportId] && (
                                  <button className="btn" style={{ fontSize: 8.5, padding: "1px 6px" }}
                                    onClick={() => reveal(r.exportId)}>
                                    {revealed[r.exportId] !== undefined ? "hide" : "reveal"}
                                  </button>
                                )}
                                {!results && (
                                  <button className="btn" style={{ fontSize: 8.5, padding: "1px 6px" }}
                                    onClick={() => setEditing((x) => ({ ...x, [r.exportId]: !editing[r.exportId] }))}>
                                    {editing[r.exportId] ? "done" : r.hasValue ? "replace" : "set value"}
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                          {r.detectedType === "document" && (
                            <>
                              <div style={{ fontSize: 9.5, color: "var(--text3)" }}>
                                {(contents[r.exportId] || "").length || r.contentLength || 0} chars
                                {r.imageCount > 0 && ` · ${r.imageCount} image${r.imageCount === 1 ? "" : "s"}`}
                                {r.imageCount > 0 && (
                                  (r.imageFiles || []).every((f) => images[String(f).split("/").pop()])
                                    ? <span style={{ color: "var(--ops)" }}> · attached</span>
                                    : <span style={{ color: "var(--cal)" }}> · not attached</span>
                                )}
                              </div>
                              <button className="btn" style={{ fontSize: 8.5, padding: "1px 6px", marginTop: 3 }}
                                onClick={() => setExpanded((x) => ({ ...x, [r.exportId]: !open }))}>
                                {open ? "hide body" : "show body"}
                              </button>
                              {open && (
                                <textarea
                                  value={contents[r.exportId] || ""}
                                  onChange={(e) => { setContents((c) => ({ ...c, [r.exportId]: e.target.value })); setPlan(null); }}
                                  rows={8}
                                  style={{ width: "100%", marginTop: 4, fontSize: 10, fontFamily: "var(--ff-mono)",
                                           border: "1px solid var(--border)", borderRadius: 4, padding: 6 }} />
                              )}
                            </>
                          )}
                          {r.detectedType === "configuration" && (
                            <>
                              <div style={{ fontSize: 10 }}>{r.deviceName}</div>
                              {r.assetDiscoveryId && (
                                <div style={{ fontSize: 9, color: "var(--text3)" }}>asset-discovery {r.assetDiscoveryId}</div>
                              )}
                              {r.referencedBy?.length > 0 && (
                                <div style={{ fontSize: 8.5, color: "var(--text3)" }}>
                                  referenced by {r.referencedBy.map((x) => x.name).filter(Boolean).join(", ")}
                                </div>
                              )}
                            </>
                          )}
                          {!r.detectedType && (
                            <span style={{ fontSize: 9.5, color: "var(--cal)" }}>
                              not a v1 type (flexible asset / contact / domain)
                            </span>
                          )}
                        </td>

                        <td style={td}>
                          <select value={r.targetType} onChange={(e) => setType(r, e.target.value)}
                            style={{ fontSize: 10, padding: "3px 5px", border: "1px solid var(--border)", borderRadius: 4 }}>
                            {["password", "document", "configuration", "skip"].map((t) => (
                              <option key={t} value={t}>{TYPE_LABEL[t]}</option>
                            ))}
                          </select>
                        </td>

                        <td style={td}>
                          {r.targetType === "configuration" ? (
                            <>
                              <select value={r.action} onChange={(e) => patchRow(r.exportId, { action: e.target.value })}
                              style={{ fontSize: 10, padding: "3px 5px", border: "1px solid var(--border)", borderRadius: 4 }}>
                                <option value="match">Match existing</option>
                                <option value="create">Create anyway</option>
                                <option value="skip">Skip</option>
                              </select>
                              {r.action === "create" && (
                                <div style={{ marginTop: 4 }}>
                                  <select value={r.configurationTypeId || ""}
                                    onChange={(e) => patchRow(r.exportId, { configurationTypeId: e.target.value })}
                                    style={{ fontSize: 9.5, padding: "2px 4px", border: `1px solid ${r.configurationTypeId ? "var(--border)" : "var(--danger)"}`, borderRadius: 4, maxWidth: 150 }}>
                                    <option value="">— type required —</option>
                                    {(configTypes?.types || []).map((t) => (
                                      <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </>
                          ) : r.targetType === "skip" ? (
                            <span style={{ fontSize: 10, color: "var(--text3)" }}>—</span>
                          ) : (
                            <select value={r.action} onChange={(e) => patchRow(r.exportId, { action: e.target.value })}
                                style={{ fontSize: 10, padding: "3px 5px", border: "1px solid var(--border)", borderRadius: 4 }}>
                              <option value="create">Create</option>
                              <option value="skip">Skip</option>
                            </select>
                          )}
                        </td>

                        <td style={{ ...td, maxWidth: 300 }}>
                          {res ? (
                            <>
                              <div style={{ fontWeight: 700, fontSize: 9.5, color: (STATUS_STYLE[res.status] || {}).color || "var(--text3)" }}>
                                {(STATUS_STYLE[res.status] || {}).label || res.status}
                                {res.itGlueId ? ` · ${res.itGlueId}` : ""}
                              </div>
                              {res.checkedAt && (
                                <div style={{ fontSize: 8.5, color: "var(--text3)" }}>
                                  body verified on the {res.checkedAt === "post-create" ? "read after create" : "read after publish"}
                                </div>
                              )}
                              {res.message && <div style={{ fontSize: 9, color: "var(--text3)", lineHeight: 1.45 }}>{res.message}</div>}
                            </>
                          ) : p ? (
                            <>
                              <div style={{ fontWeight: 700, fontSize: 9.5, color: (RESOLUTION_STYLE[p.resolution] || {}).color || "var(--text3)" }}>
                                {(RESOLUTION_STYLE[p.resolution] || {}).label || p.resolution}
                                {p.matchedName ? ` → ${p.matchedName} (${p.matchedOn})` : ""}
                              </div>
                              {(p.warnings || []).map((w, i) => (
                                <div key={i} style={{ fontSize: 9, color: "var(--cal)", lineHeight: 1.45 }}>{w}</div>
                              ))}
                            </>
                          ) : (
                            (r.flags || []).map((f, i) => (
                              <div key={i} style={{ fontSize: 9, color: "var(--cal)", lineHeight: 1.45 }}>{f}</div>
                            ))
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── 4. Dry run + import ─────────────────────────────────────────── */}
        {staging && (
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ ...hdr, marginBottom: 0 }}>4 · Dry run, then import</div>
              <button className="btn" style={{ fontSize: 10, padding: "5px 12px" }}
                onClick={dryRun} disabled={!canPlan}>
                {busy === "plan" ? "Checking…" : "Dry run"}
              </button>
              <button className="btn btn-S" style={{ fontSize: 10, padding: "5px 12px" }}
                onClick={runImport} disabled={!canRun}>
                {busy === "run" ? "Importing…" : "Import"}
              </button>
              {!orgId && <span style={sub}>Pick an organisation first.</span>}
              {orgId && !plan && !results && <span style={sub}>The dry run is required — nothing here can be undone.</span>}
            </div>

            {plan && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, marginBottom: 6 }}>
                  <strong>Will create</strong>{" "}
                  {plan.totals.willCreate.password} password ·{" "}
                  {plan.totals.willCreate.document} document ·{" "}
                  {plan.totals.willCreate.configuration} configuration
                  {" — "}
                  <strong>match</strong> {plan.totals.willMatch}
                  {" · "}<strong>skip</strong> {plan.totals.skip}
                  {plan.totals.duplicate > 0 && <> · <strong>duplicate</strong> {plan.totals.duplicate}</>}
                  {plan.totals.noMatch > 0 && <> · <strong>no match</strong> {plan.totals.noMatch}</>}
                  {plan.totals.alreadyImported > 0 && <> · <strong>already imported</strong> {plan.totals.alreadyImported}</>}
                  {plan.totals.blocked > 0 && <> · <strong style={{ color: "var(--danger)" }}>blocked {plan.totals.blocked}</strong></>}
                </div>
                {plan.notes.map((n, i) => <Banner key={i} kind={plan.canRun ? "warn" : "danger"}>{n}</Banner>)}
              </div>
            )}

            {results && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11, marginBottom: 6 }}>
                  {Object.entries(results.tally).map(([k, v]) => (
                    <span key={k} style={{ marginRight: 12, color: (STATUS_STYLE[k] || {}).color || "var(--text3)" }}>
                      <strong>{v}</strong> {(STATUS_STYLE[k] || {}).label || k}
                    </span>
                  ))}
                </div>
                {results.documentsAbandoned && <Banner kind="danger">{results.documentsAbandoned}</Banner>}
                <div style={sub}>
                  Per-row outcomes are in the Result column above. Re-running this upload resumes where
                  it stopped — rows already created are skipped, not duplicated.
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ ...sub, padding: "0 4px 20px" }}>
          Not in v1: <strong>flexible assets</strong> (Firewall, Wi-Fi, Server, Licensing and the rest of
          IT Glue's asset types — a different API with a per-type field schema, and none of the sample
          runbook's entries is one), document folders (unavailable to an API key), and editing or
          deleting anything already imported (IT Glue's API cannot do either).
        </div>
      </div>
    </div>
  );
}
